import React, { useEffect, useState } from 'react';
import {
  Button,
  Card,
  CardContent,
  MenuDropdown,
  MenuDropdownItem,
  Table,
} from '@energybox/react-ui-library/dist/components';
import {
  Equipment,
  OpacityIndex,
} from '@energybox/react-ui-library/dist/types';
import {
  ExtraShortSkeletonCell,
  ShortSkeletonCell,
  ShortMediumSkeletonCell,
} from '@energybox/react-ui-library/dist/components';
import { TableWrapper } from '../../components/ui/Table';
import PageContentHeader from '../../components/Page/PageContentHeader/PageContentHeader';
import { checkCommonPlural } from '../../util';
import { getDistributionPanelsBySiteId } from '../../actions/distribution_panel';
import { ApplicationState } from '../../reducers';
import { pathOr } from 'ramda';
import { connect } from 'react-redux';
import { getEnergyProsByDistributionPanelId } from '../../actions/energy_pros';
import { global, isDefined } from '@energybox/react-ui-library/dist/utils';
import {
  DerivedEnergyBreakerData,
  DerivedEnergyFields,
  DerivedEnergyStateByEquipmentId,
} from '../../reducers/equipment';
import {
  deleteDerivedEnergyDP,
  getDerivedEnergy,
} from '../../actions/equipment';
import EditDerivedEnergyConsumptionModal from './EditDerivedEnergyConsumptionModal';
import { Link } from 'react-router-dom';
import { Routes } from '../../routes';
import { useConfirmationModal } from '../../hooks/useConfirmationModal';

interface DerivedEnergyConsumptionRowData extends DerivedEnergyFields {
  distributionPanelId: string;
}

interface OwnProps {
  siteId: string | undefined;
  equipment: Equipment;
  isLoading?: boolean;
}

interface Props extends OwnProps {
  derivedEnergy: DerivedEnergyStateByEquipmentId;
  getDerivedEnergy: (id: string) => void;
  loadDistributionPanels: (siteId: string) => void;
  getEnergyProsByDistributionPanelId: (distributionPanelId: string) => void;
  deleteDerivedEnergyDP: (id: string, dpId: string) => void;
}

const MAX_VISIBLE_BREAKERS = 3;

const BreakerList: React.FC<{
  breakers: DerivedEnergyBreakerData[];
  maxVisible?: number;
}> = ({ breakers, maxVisible = MAX_VISIBLE_BREAKERS }) => {
  const [isExpanded, setIsExpanded] = useState(false);
  const shouldShowExpand = breakers.length > maxVisible;
  const visibleBreakers = isExpanded ? breakers : breakers.slice(0, maxVisible);

  if (breakers.length === 0) return <div>{global.NOT_AVAILABLE}</div>;

  return (
    <div>
      {visibleBreakers.map((breaker, index) => (
        <div key={breaker.id} style={{ marginBottom: '2px' }}>
          {breaker.title}
        </div>
      ))}
      {shouldShowExpand && (
        <Button
          variant='text'
          size='small'
          onClick={() => setIsExpanded(!isExpanded)}
        >
          {isExpanded ? (
            <>Show Less</>
          ) : (
            <>+{breakers.length - maxVisible} More</>
          )}
        </Button>
      )}
    </div>
  );
};

export const sortBreakersByTitle = (aTitle: string, bTitle: string) => {
  if (!aTitle.startsWith('Breaker')) return 1;
  if (!bTitle.startsWith('Breaker')) return -1;
  if (aTitle.startsWith('Breaker') && bTitle.startsWith('Breaker')) {
    const aNumber = aTitle.match(/\d+/)?.[0];
    const bNumber = bTitle.match(/\d+/)?.[0];
    if (aNumber && bNumber) {
      return parseInt(aNumber) - parseInt(bNumber);
    }
  }
  return aTitle.localeCompare(bTitle);
};

export const sortMainBreakersByPort = (
  aPort: number | undefined,
  bPort: number | undefined
) => {
  if (aPort === undefined) return 1;
  if (bPort === undefined) return -1;
  return aPort - bPort;
};

const DerivedEnergyConsumptionTable: React.FC<Props> = ({
  siteId,
  equipment,
  isLoading = false,
  getDerivedEnergy,
  derivedEnergy,
  loadDistributionPanels,
  getEnergyProsByDistributionPanelId,
  deleteDerivedEnergyDP,
}) => {
  const [editingDPId, setEditingDPId] = useState<string | null>(null);
  const [showEditDP, setShowEditDP] = useState(false);
  const [isNew, setIsNew] = useState(false);
  const { openModal, ConfirmationModal } = useConfirmationModal();

  useEffect(() => {
    siteId && loadDistributionPanels(siteId);
  }, [siteId, loadDistributionPanels]);

  useEffect(() => {
    getDerivedEnergy(String(equipment.id));
  }, [equipment.id, getDerivedEnergy]);

  useEffect(() => {
    if (isDefined(editingDPId)) {
      getEnergyProsByDistributionPanelId(editingDPId);
    }
  }, [editingDPId, getEnergyProsByDistributionPanelId]);

  const tableData: DerivedEnergyConsumptionRowData[] = derivedEnergy?.derivedEnergyByDistributionPanelId
    ? Object.keys(derivedEnergy.derivedEnergyByDistributionPanelId)
        .map(dpId => {
          const {
            distributionPanelTitle,
            mainBreakerSensorsToAdd,
            mainBreakerSensorsToRemove,
            breakersToAdd,
            breakersToRemove,
          } = derivedEnergy.derivedEnergyByDistributionPanelId[dpId] || {};
          return {
            distributionPanelId: dpId,
            distributionPanelTitle: distributionPanelTitle || '',
            mainBreakerSensorsToAdd: mainBreakerSensorsToAdd || [],
            mainBreakerSensorsToRemove: mainBreakerSensorsToRemove || [],
            breakersToAdd: breakersToAdd || [],
            breakersToRemove: breakersToRemove || [],
          };
        })
        .sort((a, b) =>
          a.distributionPanelTitle.localeCompare(b.distributionPanelTitle)
        )
    : [];

  const showNewDerivedEnergyDPModal = () => {
    setIsNew(true);
    setShowEditDP(true);
  };

  const handleEdit = (rowData: DerivedEnergyConsumptionRowData) => {
    setIsNew(false);
    setShowEditDP(true);
    setEditingDPId(rowData.distributionPanelId);
  };

  const handleDelete = (rowData: DerivedEnergyConsumptionRowData) => {
    openModal(
      {
        title: 'Delete Distribution Panel',
        description: `Are you sure you want to delete "${rowData.distributionPanelTitle}" from this equipment's derived energy configuration?`,
        confirmText: 'Delete',
        cancelText: 'Cancel',
      },
      () => {
        deleteDerivedEnergyDP(
          String(equipment.id),
          rowData.distributionPanelId
        );
      }
    );
  };

  const handleModalClose = () => {
    setShowEditDP(false);
  };

  const columns = [
    {
      width: '15%',
      header: 'Name',
      cellContent: (row: DerivedEnergyConsumptionRowData) => {
        return (
          <Link
            to={`${Routes.DISTRIBUTION_PANELS}/${row.distributionPanelId}`}
            target='_blank'
          >
            {row.distributionPanelTitle}
          </Link>
        );
      },
      skeletonCellContent: (rowIndex: OpacityIndex) => (
        <ShortMediumSkeletonCell opacityIndex={rowIndex} />
      ),
    },
    {
      width: '20%',
      header: 'Main Breakers to Add',
      cellContent: (row: DerivedEnergyConsumptionRowData) => {
        return (
          <BreakerList
            breakers={row.mainBreakerSensorsToAdd.sort((a, b) =>
              sortMainBreakersByPort(a.port, b.port)
            )}
          />
        );
      },
      skeletonCellContent: (rowIndex: OpacityIndex) => (
        <ShortSkeletonCell opacityIndex={rowIndex} />
      ),
    },
    {
      width: '20%',
      header: 'Main Breakers to Remove',
      cellContent: (row: DerivedEnergyConsumptionRowData) => {
        return (
          <BreakerList
            breakers={row.mainBreakerSensorsToRemove.sort((a, b) =>
              sortMainBreakersByPort(a.port, b.port)
            )}
          />
        );
      },
      skeletonCellContent: (rowIndex: OpacityIndex) => (
        <ShortSkeletonCell opacityIndex={rowIndex} />
      ),
    },
    {
      width: '20%',
      header: 'Breakers to Add',
      cellContent: (row: DerivedEnergyConsumptionRowData) => {
        return (
          <BreakerList
            breakers={row.breakersToAdd.sort((a, b) =>
              sortBreakersByTitle(a.title, b.title)
            )}
          />
        );
      },
      skeletonCellContent: (rowIndex: OpacityIndex) => (
        <ShortSkeletonCell opacityIndex={rowIndex} />
      ),
    },
    {
      width: '20%',
      header: 'Breakers to Remove',
      cellContent: (row: DerivedEnergyConsumptionRowData) => {
        return (
          <BreakerList
            breakers={row.breakersToRemove.sort((a, b) =>
              sortBreakersByTitle(a.title, b.title)
            )}
          />
        );
      },
      skeletonCellContent: (rowIndex: OpacityIndex) => (
        <ShortSkeletonCell opacityIndex={rowIndex} />
      ),
    },
    {
      width: '5%',
      header: '',
      cellContent: (row: DerivedEnergyConsumptionRowData) => {
        return (
          <MenuDropdown>
            <MenuDropdownItem onSelect={() => handleEdit(row)}>
              Edit Distribution Panel
            </MenuDropdownItem>
            <MenuDropdownItem isRed onSelect={() => handleDelete(row)}>
              Delete Distribution Panel
            </MenuDropdownItem>
          </MenuDropdown>
        );
      },
      skeletonCellContent: (rowIndex: OpacityIndex) => (
        <ExtraShortSkeletonCell opacityIndex={rowIndex} />
      ),
    },
  ];

  return (
    <>
      <PageContentHeader header='Derived Energy Monitoring'>
        <div style={{ display: 'flex', justifyContent: 'center' }}>
          <Button variant='solid' onClick={showNewDerivedEnergyDPModal}>
            Attach Distribution Panel
          </Button>
        </div>
      </PageContentHeader>
      <div style={{ marginTop: '1rem' }} />
      <TableWrapper
        header={checkCommonPlural('Distribution Panel', tableData.length)}
        pageNavHidden={true}
      >
        <Table
          highlightAlternateRows={true}
          columns={columns}
          data={tableData}
          uniqueKeyField='distributionPanelId'
          dataIsLoading={isLoading}
        />
      </TableWrapper>
      {tableData.length === 0 && (
        // show a centered message that no distribution panels are set up
        <div
          style={{
            marginTop: '2rem',
            marginBottom: '2rem',
            display: 'flex',
            justifyContent: 'center',
          }}
        >
          <div style={{ fontSize: '0.75rem', textAlign: 'center' }}>
            No Derived Energy is set up for this equipment.
          </div>
        </div>
      )}
      <EditDerivedEnergyConsumptionModal
        isNew={isNew}
        isVisible={showEditDP}
        siteId={siteId || ''}
        equipmentId={String(equipment.id)}
        editingDPId={editingDPId}
        onClose={handleModalClose}
      />
      <ConfirmationModal />
    </>
  );
};

const mapStateToProps = (
  {
    distributionPanels,
    energyPros,
    equipment: equipmentState,
  }: ApplicationState,
  { siteId, equipment }: OwnProps
) => ({
  derivedEnergy:
    equipmentState.derivedEnergyByEquipmentId[String(equipment.id)],
  distributionPanels: pathOr(
    [],
    [],
    siteId ? distributionPanels.distributionPanelsBySiteId[siteId] : []
  ),
  energyProsById: energyPros.energyProsById,
  energyProIdsByDistributionPanelId:
    energyPros.energyProIdsByDistributionPanelId,
});

const mapDispatchToProps = {
  getDerivedEnergy: getDerivedEnergy,
  loadDistributionPanels: getDistributionPanelsBySiteId,
  getEnergyProsByDistributionPanelId: getEnergyProsByDistributionPanelId,
  deleteDerivedEnergyDP: deleteDerivedEnergyDP,
};

export default connect(
  mapStateToProps,
  mapDispatchToProps
)(DerivedEnergyConsumptionTable);
